import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  ChevronLeft, 
  ChevronRight, 
  Save, 
  CheckCircle2, 
  X,
  Scale,
  FileText,
  AlertTriangle,
  Users,
  Briefcase,
  Target,
  Check
} from 'lucide-react';
import {
  US_ENTRY_COUNT_OPTIONS,
  ICE_CHECKIN_TIMELINE,
  IMMIGRATION_PROCESS_TYPES,
  PROCESS_STATUS_OPTIONS,
} from '@/lib/qualificationOptions';
import { useQualificationOptionsByKey } from '@/hooks/useQualificationOptions';
import { US_STATES, BR_STATES } from '@/lib/locationUtils';
import { ChildrenList, type Child } from './ChildrenList';
import { USEntriesList, type USEntry } from './USEntriesList';
import { MonthYearPicker } from './MonthYearPicker';
import { useQualification } from '@/hooks/useQualification';
import { cn } from '@/lib/utils';

interface QualificationModalProps {
  open: boolean;
  onClose: () => void;
  dealId: string;
  contactId: string | null;
  contactName: string;
  onComplete: () => void;
}

const STEPS = [
  { id: 'entry', label: 'Entrada', icon: Scale },
  { id: 'docs', label: 'Documentos', icon: FileText },
  { id: 'removal', label: 'Remoção', icon: AlertTriangle },
  { id: 'family', label: 'Família', icon: Users },
  { id: 'processes', label: 'Processos', icon: Briefcase },
  { id: 'qualification', label: 'Qualificação', icon: Target },
];

export function QualificationModal({ 
  open, 
  onClose, 
  dealId, 
  contactId, 
  contactName,
  onComplete 
}: QualificationModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  
  // Dynamic qualification options from database
  const { data: genderOptions = [] } = useQualificationOptionsByKey('gender_options');
  const { data: languageOptions = [] } = useQualificationOptionsByKey('language_options');
  const { data: countryOptions = [] } = useQualificationOptionsByKey('country_options');
  const { data: entryMethods = [] } = useQualificationOptionsByKey('us_entry_methods');
  const { data: eadStatusOptions = [] } = useQualificationOptionsByKey('ead_status_options');
  const { data: maritalStatusOptions = [] } = useQualificationOptionsByKey('marital_status_options');
  const { data: spouseImmigrationStatus = [] } = useQualificationOptionsByKey('spouse_immigration_status');
  const { data: currentImmigrationStatus = [] } = useQualificationOptionsByKey('current_immigration_status');
  const { data: qualificationObjectives = [] } = useQualificationOptionsByKey('qualification_objectives');
  const { data: urgencyOptions = [] } = useQualificationOptionsByKey('urgency_options');
  const { data: reasonSeekingNewService = [] } = useQualificationOptionsByKey('reason_seeking_new_service');
  
  const {
    contactData,
    immigrationProcesses,
    dealQualification,
    loading,
    saving,
    saveContactData,
    saveDealQualification,
    reload,
  } = useQualification(dealId, contactId);

  // Initialize form data from loaded data
  useEffect(() => {
    if (contactData || dealQualification) {
      setFormData({
        // Personal data
        gender: contactData?.gender || '',
        language: contactData?.language || '',
        address_city: contactData?.address_city || '',
        address_state: contactData?.address_state || '',
        address_country_code: contactData?.address_country_code || 'US',
        // Contact fields - updated for new schema
        us_entry_method: contactData?.us_entry_method || '',
        us_first_entry_month: contactData?.us_first_entry_month || null,
        us_first_entry_year: contactData?.us_first_entry_year || null,
        us_entry_count: contactData?.us_entry_count || '1',
        us_entries_data: contactData?.us_entries_data || [],
        had_visa_denied: contactData?.had_visa_denied || false,
        has_ssn: contactData?.has_ssn || false,
        has_itin: contactData?.has_itin || false,
        has_drivers_license: contactData?.has_drivers_license || false,
        has_ead: contactData?.has_ead || false,
        ead_status: contactData?.ead_status || '',
        in_removal_proceedings: contactData?.in_removal_proceedings || false,
        received_nta: contactData?.received_nta || false,
        has_deportation_order: contactData?.has_deportation_order || false,
        was_previously_deported: contactData?.was_previously_deported || false,
        previous_deportation_month: contactData?.previous_deportation_month || null,
        previous_deportation_year: contactData?.previous_deportation_year || null,
        has_ice_checkin: contactData?.has_ice_checkin || false,
        ice_checkin_timeline: contactData?.ice_checkin_timeline || '',
        marital_status: contactData?.marital_status || '',
        spouse_immigration_status: contactData?.spouse_immigration_status || '',
        has_children: contactData?.has_children || false,
        children_data: contactData?.children_data || [],
        // Deal qualification fields
        current_immigration_status: dealQualification?.current_immigration_status || '',
        objective: dealQualification?.objective || '',
        urgency: dealQualification?.urgency || '',
        urgency_reason: dealQualification?.urgency_reason || '',
        has_previous_lawyer: dealQualification?.has_previous_lawyer || false,
        had_lawyer_problems: dealQualification?.had_lawyer_problems || false,
        lawyer_problem_details: dealQualification?.lawyer_problem_details || '',
        has_active_lawyer: dealQualification?.has_active_lawyer || false,
        active_lawyer_name: dealQualification?.active_lawyer_name || '',
        reason_seeking_new_lawyer: dealQualification?.reason_seeking_new_lawyer || '',
        reason_seeking_new_lawyer_details: dealQualification?.reason_seeking_new_lawyer_details || '',
        qualification_notes: dealQualification?.qualification_notes || '',
      });
    }
  }, [contactData, dealQualification]);

  const updateField = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  const goNext = () => {
    setCompletedSteps(prev => new Set([...prev, currentStep]));
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goPrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSaveDraft = async () => {
    // Save contact data
    const contactFields = {
      gender: formData.gender || null,
      language: formData.language || null,
      address_city: formData.address_city || null,
      address_state: formData.address_state || null,
      address_country_code: formData.address_country_code || null,
      us_entry_method: formData.us_entry_method || null,
      us_first_entry_month: formData.us_first_entry_month || null,
      us_first_entry_year: formData.us_first_entry_year || null,
      us_entry_count: formData.us_entry_count || null,
      us_entries_data: formData.us_entries_data || [],
      had_visa_denied: formData.had_visa_denied,
      has_ssn: formData.has_ssn,
      has_itin: formData.has_itin,
      has_drivers_license: formData.has_drivers_license,
      has_ead: formData.has_ead,
      ead_status: formData.ead_status || null,
      in_removal_proceedings: formData.in_removal_proceedings,
      received_nta: formData.received_nta,
      has_deportation_order: formData.has_deportation_order,
      was_previously_deported: formData.was_previously_deported,
      previous_deportation_month: formData.previous_deportation_month || null,
      previous_deportation_year: formData.previous_deportation_year || null,
      has_ice_checkin: formData.has_ice_checkin,
      ice_checkin_timeline: formData.ice_checkin_timeline || null,
      marital_status: formData.marital_status || null,
      spouse_immigration_status: formData.spouse_immigration_status || null,
      has_children: formData.has_children,
      children_data: formData.children_data || [],
    };
    
    if (contactId) {
      await saveContactData(contactFields);
    }
  };

  const handleComplete = async () => {
    // Save all contact data
    await handleSaveDraft();
    
    // Save deal qualification (without budget_range)
    const qualificationFields = {
      current_immigration_status: formData.current_immigration_status || null,
      objective: formData.objective || null,
      urgency: formData.urgency || null,
      urgency_reason: formData.urgency_reason || null,
      has_previous_lawyer: formData.has_previous_lawyer,
      had_lawyer_problems: formData.had_lawyer_problems,
      lawyer_problem_details: formData.lawyer_problem_details || null,
      has_active_lawyer: formData.has_active_lawyer,
      active_lawyer_name: formData.active_lawyer_name || null,
      reason_seeking_new_lawyer: formData.reason_seeking_new_lawyer || null,
      reason_seeking_new_lawyer_details: formData.reason_seeking_new_lawyer_details || null,
      qualification_notes: formData.qualification_notes || null,
    };
    
    await saveDealQualification(qualificationFields);
    onComplete();
    onClose();
  };

  // Helper to check if entry count is greater than 1
  const entryCountValue = formData.us_entry_count || '1';
  const showEntriesList = entryCountValue !== '1';

  const renderStepContent = () => {
    switch (STEPS[currentStep].id) {
      case 'entry':
        // Get state options based on selected country
        const stateOptions = formData.address_country_code === 'BR' 
          ? Object.entries(BR_STATES).map(([code, name]) => ({ value: code, label: name }))
          : Object.entries(US_STATES).map(([code, name]) => ({ value: code, label: name }));
        
        return (
          <div className="space-y-6">
            {/* Personal Data Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Dados Pessoais</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Sexo</Label>
                  <Select value={formData.gender || ''} onValueChange={(v) => updateField('gender', v)}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                    <SelectContent>
                      {genderOptions.map((opt: { value: string; label: string }) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Idioma</Label>
                  <Select value={formData.language || ''} onValueChange={(v) => updateField('language', v)}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                    <SelectContent>
                      {languageOptions.map((opt: { value: string; label: string }) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">País</Label>
                  <Select value={formData.address_country_code || 'US'} onValueChange={(v) => {
                    updateField('address_country_code', v);
                    updateField('address_state', ''); // Reset state when country changes
                  }}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                    <SelectContent>
                      {countryOptions.map((opt: { value: string; label: string }) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Estado</Label>
                  <Select value={formData.address_state || ''} onValueChange={(v) => updateField('address_state', v)}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                    <SelectContent>
                      {stateOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-sm">Cidade</Label>
                  <Input 
                    className="mt-1" 
                    placeholder="Digite a cidade" 
                    value={formData.address_city || ''} 
                    onChange={(e) => updateField('address_city', e.target.value)} 
                  />
                </div>
              </div>
            </div>
            
            <Separator />

            {/* Entry to USA Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Entrada nos EUA</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Forma de Entrada</Label>
                  <Select value={formData.us_entry_method || ''} onValueChange={(v) => updateField('us_entry_method', v)}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                    <SelectContent>
                      {entryMethods.map((opt: { value: string; label: string }) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <MonthYearPicker
                  label="Data da Primeira Entrada"
                  month={formData.us_first_entry_month}
                  year={formData.us_first_entry_year}
                  onMonthChange={(v) => updateField('us_first_entry_month', v)}
                  onYearChange={(v) => updateField('us_first_entry_year', v)}
                />
                <div>
                  <Label className="text-sm">Quantas vezes entrou?</Label>
                  <Select value={formData.us_entry_count || '1'} onValueChange={(v) => updateField('us_entry_count', v)}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                    <SelectContent>
                      {US_ENTRY_COUNT_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-3 mt-6">
                  <Switch checked={formData.had_visa_denied} onCheckedChange={(v) => updateField('had_visa_denied', v)} />
                  <Label>Já teve visto negado?</Label>
                </div>
              </div>
              {showEntriesList && (
                <div>
                  <Label className="text-sm mb-2 block">Histórico de Entradas Posteriores</Label>
                  <USEntriesList 
                    entries={formData.us_entries_data || []} 
                    onChange={(entries) => updateField('us_entries_data', entries)} 
                  />
                </div>
              )}
            </div>
          </div>
        );

      case 'docs':
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Documentos</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2">
                <Switch checked={formData.has_ssn} onCheckedChange={(v) => updateField('has_ssn', v)} />
                <Label>SSN</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={formData.has_itin} onCheckedChange={(v) => updateField('has_itin', v)} />
                <Label>ITIN</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={formData.has_drivers_license} onCheckedChange={(v) => updateField('has_drivers_license', v)} />
                <Label>Carteira</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={formData.has_ead} onCheckedChange={(v) => updateField('has_ead', v)} />
                <Label>EAD</Label>
              </div>
            </div>
            {formData.has_ead && (
              <div className="p-3 bg-muted rounded-lg">
                <div className="w-48">
                  <Label className="text-sm">Status EAD</Label>
                  <Select value={formData.ead_status || ''} onValueChange={(v) => updateField('ead_status', v)}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      {eadStatusOptions.map((opt: { value: string; label: string }) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        );

      case 'removal':
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Situação de Remoção</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Switch checked={formData.in_removal_proceedings} onCheckedChange={(v) => updateField('in_removal_proceedings', v)} />
                <Label>Em processo de remoção</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={formData.received_nta} onCheckedChange={(v) => updateField('received_nta', v)} />
                <Label>Recebeu NTA</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={formData.has_deportation_order} onCheckedChange={(v) => updateField('has_deportation_order', v)} />
                <Label>Ordem de deportação</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={formData.was_previously_deported} onCheckedChange={(v) => updateField('was_previously_deported', v)} />
                <Label>Já foi deportado</Label>
              </div>
            </div>
            {formData.was_previously_deported && (
              <MonthYearPicker
                label="Data da deportação"
                month={formData.previous_deportation_month}
                year={formData.previous_deportation_year}
                onMonthChange={(v) => updateField('previous_deportation_month', v)}
                onYearChange={(v) => updateField('previous_deportation_year', v)}
              />
            )}
            <div className="flex items-center gap-2">
              <Switch checked={formData.has_ice_checkin} onCheckedChange={(v) => updateField('has_ice_checkin', v)} />
              <Label>Check-in no ICE</Label>
            </div>
            {formData.has_ice_checkin && (
              <div>
                <Label className="text-sm">Próximo check-in</Label>
                <Select value={formData.ice_checkin_timeline || ''} onValueChange={(v) => updateField('ice_checkin_timeline', v)}>
                  <SelectTrigger className="mt-1 w-64"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    {ICE_CHECKIN_TIMELINE.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        );

      case 'family':
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Família</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Estado Civil</Label>
                <Select value={formData.marital_status || ''} onValueChange={(v) => updateField('marital_status', v)}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    {maritalStatusOptions.map((opt: { value: string; label: string }) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(formData.marital_status === 'married' || formData.marital_status === 'domestic_partnership') && (
                <div>
                  <Label className="text-sm">Status Imigratório do Cônjuge</Label>
                  <Select value={formData.spouse_immigration_status || ''} onValueChange={(v) => updateField('spouse_immigration_status', v)}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                    <SelectContent>
                      {spouseImmigrationStatus.map((opt: { value: string; label: string }) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={formData.has_children} onCheckedChange={(v) => {
                updateField('has_children', v);
                if (!v) updateField('children_data', []);
              }} />
              <Label>Tem filhos</Label>
            </div>
            {formData.has_children && (
              <div className="p-3 bg-muted rounded-lg">
                <Label className="text-sm mb-2 block">Lista de Filhos</Label>
                <ChildrenList 
                  children={formData.children_data || []} 
                  onChange={(children) => updateField('children_data', children)} 
                />
              </div>
            )}
          </div>
        );

      case 'processes':
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Processos Imigratórios Anteriores</h3>
            {immigrationProcesses.length > 0 ? (
              <div className="space-y-2">
                {immigrationProcesses.map(process => (
                  <div key={process.id} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                    <Badge variant="outline">
                      {IMMIGRATION_PROCESS_TYPES.find(t => t.value === process.process_type)?.label || process.process_type}
                    </Badge>
                    {process.process_status && (
                      <Badge variant={process.process_status === 'approved' ? 'default' : 'secondary'}>
                        {PROCESS_STATUS_OPTIONS.find(s => s.value === process.process_status)?.label || process.process_status}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">Nenhum processo anterior registrado. Você pode adicionar na tab de Qualificação após concluir.</p>
            )}
          </div>
        );

      case 'qualification':
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Qualificação da Oportunidade</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Status Imigratório Atual</Label>
                <Select value={formData.current_immigration_status || ''} onValueChange={(v) => updateField('current_immigration_status', v)}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    {currentImmigrationStatus.map((opt: { value: string; label: string }) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Objetivo</Label>
                <Select value={formData.objective || ''} onValueChange={(v) => updateField('objective', v)}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    {qualificationObjectives.map((opt: { value: string; label: string }) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Urgência</Label>
                <Select value={formData.urgency || ''} onValueChange={(v) => updateField('urgency', v)}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    {urgencyOptions.map((opt: { value: string; label: string }) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <Separator />
            
            <div className="space-y-3">
              <Label className="font-medium">Histórico com Escritórios de Imigração</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="flex items-center gap-2">
                  <Switch checked={formData.has_previous_lawyer} onCheckedChange={(v) => updateField('has_previous_lawyer', v)} />
                  <Label className="text-sm">Já contratou escritório/assessoria?</Label>
                </div>
                {formData.has_previous_lawyer && (
                  <div className="flex items-center gap-2">
                    <Switch checked={formData.had_lawyer_problems} onCheckedChange={(v) => updateField('had_lawyer_problems', v)} />
                    <Label className="text-sm">Teve problemas com o serviço?</Label>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Switch checked={formData.has_active_lawyer} onCheckedChange={(v) => updateField('has_active_lawyer', v)} />
                  <Label className="text-sm">Tem escritório/assessoria ativo?</Label>
                </div>
              </div>
              
              {formData.has_active_lawyer && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg space-y-3 border border-amber-200 dark:border-amber-800">
                  <div>
                    <Label className="text-sm">Nome do Escritório/Assessoria Atual</Label>
                    <Input className="mt-1" placeholder="Ex: Law Office of John Smith" value={formData.active_lawyer_name || ''} onChange={(e) => updateField('active_lawyer_name', e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-sm">Por que está buscando outro serviço?</Label>
                    <Select value={formData.reason_seeking_new_lawyer || ''} onValueChange={(v) => updateField('reason_seeking_new_lawyer', v)}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                      <SelectContent>
                        {reasonSeekingNewService.map((opt: { value: string; label: string }) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
            
            <div>
              <Label className="text-sm">Notas da Qualificação</Label>
              <Textarea className="mt-1" placeholder="Observações importantes..." value={formData.qualification_notes || ''} onChange={(e) => updateField('qualification_notes', e.target.value)} rows={3} />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="border-b pb-4">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl">Qualificação SDR</DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">{contactName}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="pt-4">
            <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
              <span>Progresso</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        </DialogHeader>

        <div className="flex gap-6 mt-4">
          {/* Sidebar */}
          <div className="hidden md:flex flex-col gap-1 w-48 shrink-0">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const isCompleted = completedSteps.has(index);
              const isCurrent = currentStep === index;
              
              return (
                <button
                  key={step.id}
                  onClick={() => setCurrentStep(index)}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors",
                    isCurrent && "bg-primary text-primary-foreground",
                    !isCurrent && isCompleted && "text-foreground hover:bg-muted",
                    !isCurrent && !isCompleted && "text-muted-foreground hover:bg-muted"
                  )}
                >
                  {isCompleted && !isCurrent ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Icon className="w-4 h-4" />
                  )}
                  {step.label}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              renderStepContent()
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t mt-6">
          <div className="flex gap-2">
            <Button variant="outline" onClick={goPrev} disabled={currentStep === 0}>
              <ChevronLeft className="w-4 h-4 mr-1" />
              Voltar
            </Button>
            {currentStep < STEPS.length - 1 && (
              <Button onClick={goNext}>
                Próximo
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleSaveDraft} disabled={saving}>
              <Save className="w-4 h-4 mr-1" />
              Salvar Rascunho
            </Button>
            <Button 
              onClick={handleComplete} 
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <CheckCircle2 className="w-4 h-4 mr-1" />
              Concluir Qualificação
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
